import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowRight,
  BarChart3,
  ChevronDown,
  History,
  PencilLine,
  Plus,
  RefreshCcw,
  ShieldCheck,
  Wallet,
} from 'lucide-react';
import {
  connectIB,
  connectMT5,
  fetchAccounts,
  fetchRiskSettings,
  getMT5Account,
  restartInstance,
  shutdownInstance,
  startOnboardingAndWait,
} from '../api/api';
import EditAccountModal from './EditAccountModal';
import CreateAccountModal from './CreateAccountModal';
import VncModal from '../components/VncModal';
import { Card, CardContent } from '../components/ui/card';
import { onboardStart } from '../api/ctraderApi';

const connectionTone = {
  connected: 'border-emerald-500/20 bg-emerald-500/10 text-emerald-300',
  active: 'border-emerald-500/20 bg-emerald-500/10 text-emerald-300',
  connecting: 'border-amber-500/20 bg-amber-500/10 text-amber-300',
  pending: 'border-amber-500/20 bg-amber-500/10 text-amber-300',
  disconnected: 'border-rose-500/20 bg-rose-500/10 text-rose-300',
  inactive: 'border-rose-500/20 bg-rose-500/10 text-rose-300',
  unknown: 'border-borderDefault bg-backgroundInput text-textSecondary',
};

const platformTone = {
  mt5: 'border-sky-500/20 bg-sky-500/10 text-sky-300',
  ctrader: 'border-violet-500/20 bg-violet-500/10 text-violet-300',
  interactivebrokers: 'border-rose-500/20 bg-rose-500/10 text-rose-300',
  unknown: 'border-borderDefault bg-backgroundInput text-textSecondary',
};

const secondaryActionClasses = 'inline-flex items-center gap-2 rounded-lg border border-borderDefault bg-backgroundInput px-3 py-2 text-sm font-medium text-textPrimary transition hover:border-primary/30 hover:bg-primary/5';
const primaryActionClasses = 'inline-flex items-center gap-2 rounded-lg bg-primary px-3 py-2 text-sm font-medium text-textAccent transition hover:bg-primary/90';
const FIRST_ACCOUNT_SKIP_KEY = 'valpha:first-account-onboarding-skipped';

const FIRST_ACCOUNT_PLATFORMS = [
  {
    value: 'MT5',
    label: 'MetaTrader 5',
    badge: 'Fastest setup',
    summary: 'Best when you already have your MT5 login details.',
    requirements: ['Account number', 'Password', 'Server'],
  },
  {
    value: 'cTrader',
    label: 'cTrader',
    badge: 'OAuth flow',
    summary: 'Best when your broker already uses cTrader.',
    requirements: ['Browser sign-in', 'Linked account'],
  },
  {
    value: 'InteractiveBrokers',
    label: 'Interactive Brokers',
    badge: 'Paper or live',
    summary: 'Use your IB credentials for either paper or live trading.',
    requirements: ['Username', 'Password', 'Account ID', 'Paper or live'],
  },
];

function normalizeAccounts(response) {
  const list = Array.isArray(response) ? response : response?.results || response?.data || [];
  return Array.isArray(list) ? list : [];
}

function getConnectionState(account) {
  return (account?.status || account?.connection_status || 'unknown').toLowerCase();
}

function getPlatformTone(platform) {
  return platformTone[(platform || 'unknown').toLowerCase()] || platformTone.unknown;
}

function formatCurrency(value, currency = 'USD') {
  const numericValue = Number.parseFloat(value);
  if (!Number.isFinite(numericValue)) return '-';

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(numericValue);
}

function formatSignedCurrency(value, currency = 'USD') {
  const numericValue = Number.parseFloat(value);
  if (!Number.isFinite(numericValue)) return '-';

  const base = formatCurrency(Math.abs(numericValue), currency);
  return `${numericValue >= 0 ? '+' : '-'}${base}`;
}

function buildAttentionReasons(account, riskConfigured) {
  const reasons = [];
  const connectionState = getConnectionState(account);

  if (connectionState === 'disconnected' || connectionState === 'inactive') {
    reasons.push('Connection offline');
  }

  if (connectionState === 'connecting' || connectionState === 'pending') {
    reasons.push('Connection in progress');
  }

  if (riskConfigured === false) {
    reasons.push('Risk settings incomplete');
  }

  return reasons;
}

function MetricCard({ icon: iconComponent, label, value, hint }) {
  return (
    <Card className="h-full overflow-hidden rounded-2xl">
      <CardContent className="flex min-h-[118px] flex-col justify-between px-5 pb-4 pt-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-textSecondary">{label}</p>
            <p className="mt-3 text-[1.75rem] font-semibold tracking-tight text-textPrimary">{value}</p>
            {hint ? <p className="mt-1.5 text-sm leading-5 text-textSecondary">{hint}</p> : null}
          </div>
          <div className="rounded-lg border border-borderDefault bg-backgroundInput p-2">
            {React.createElement(iconComponent, { className: 'h-4 w-4 text-primary' })}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function AccountPreviewCard({
  account,
  riskConfigured,
  onOpenEdit,
  onOpenRisk,
  onOpenHistory,
  onOpenStudio,
  onToggleActions,
  onShutdown,
  onRestart,
  onConnect,
  onOpenVnc,
  isActionsOpen,
  connectingAccountId,
  vncLoading,
}) {
  const connectionState = getConnectionState(account);
  const hasConnectionState = connectionState !== 'unknown';
  const tone = connectionTone[connectionState] || connectionTone.unknown;
  const platformClassName = getPlatformTone(account.platform);
  const attentionReasons = buildAttentionReasons(account, riskConfigured);
  const footerAttentionReasons = attentionReasons.filter((reason) => reason !== 'Risk settings incomplete');
  const balance = formatCurrency(account.balance, account.currency);
  const equity = formatCurrency(account.equity, account.currency);
  const freeMargin = formatCurrency(account.free_margin, account.currency);
  const profit = formatSignedCurrency(account.profit, account.currency);
  const profitValue = Number.parseFloat(account.profit);
  const profitClassName = !Number.isFinite(profitValue)
    ? 'text-textSecondary'
    : profitValue < 0
      ? 'text-destructive'
      : 'text-success';

  return (
    <Card className={`overflow-hidden ${account.is_default ? 'ring-1 ring-primary/25' : ''}`}>
      <CardContent className="p-0">
        <div className={`border-b border-borderDefault px-5 py-4 ${account.is_default ? 'bg-gradient-to-r from-primary/10 via-backgroundSurface to-backgroundSurface' : 'bg-backgroundSurface'}`}>
          <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="text-lg font-semibold tracking-tight text-textPrimary">{account.name || `Account ${account.id}`}</h3>
                {account.is_default ? (
                  <span className="rounded-full border border-primary/20 bg-primary/10 px-2.5 py-1 text-xs font-medium text-primary">
                    Default
                  </span>
                ) : null}
                {hasConnectionState ? (
                  <span className={`rounded-full border px-2.5 py-1 text-xs font-medium capitalize ${tone}`}>
                    {connectionState}
                  </span>
                ) : null}
                {account.platform ? (
                  <span className={`rounded-full border px-2.5 py-1 text-xs font-medium ${platformClassName}`}>
                    {account.platform}
                  </span>
                ) : null}
              </div>
              {account.broker ? <p className="mt-2 text-sm text-textSecondary">{account.broker}</p> : null}
            </div>

            <div className="flex flex-wrap gap-2">
              <button type="button" onClick={() => onOpenHistory(account.id)} className={secondaryActionClasses}>
                <History className="h-4 w-4 text-primary" />
                History
              </button>
              <button type="button" onClick={() => onOpenEdit(account.id)} className={secondaryActionClasses}>
                <PencilLine className="h-4 w-4 text-primary" />
                Edit
              </button>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => onToggleActions(account.id)}
                  className={`${secondaryActionClasses} ${isActionsOpen ? 'border-primary/40 bg-primary/10' : ''}`}
                >
                  Actions
                  <ChevronDown className={`h-4 w-4 text-primary transition-transform ${isActionsOpen ? 'rotate-180' : ''}`} />
                </button>
                {isActionsOpen ? (
                  <div className="absolute right-0 top-full z-20 mt-2 min-w-[11rem] overflow-hidden rounded-xl border border-borderDefault bg-backgroundSurface shadow-xl">
                    <button
                      type="button"
                      onClick={() => void onShutdown(account.id)}
                      className="block w-full px-4 py-2.5 text-left text-sm text-textPrimary transition hover:bg-backgroundInput"
                    >
                      Shutdown
                    </button>
                    <button
                      type="button"
                      onClick={() => void onRestart(account.id)}
                      className="block w-full px-4 py-2.5 text-left text-sm text-textPrimary transition hover:bg-backgroundInput"
                    >
                      Restart
                    </button>
                    <button
                      type="button"
                      onClick={() => void onConnect(account)}
                      disabled={connectingAccountId === account.id}
                      className="block w-full px-4 py-2.5 text-left text-sm text-textPrimary transition hover:bg-backgroundInput disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {connectingAccountId === account.id ? 'Connecting...' : 'Connect'}
                    </button>
                    {account.platform === 'MT5' ? (
                      <button
                        type="button"
                        onClick={() => void onOpenVnc(account)}
                        disabled={vncLoading}
                        className="block w-full px-4 py-2.5 text-left text-sm text-textPrimary transition hover:bg-backgroundInput disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {vncLoading ? 'Opening MetaTrader...' : 'MetaTrader'}
                      </button>
                    ) : null}
                  </div>
                ) : null}
              </div>
              <button type="button" onClick={() => onOpenStudio(account.id)} className={primaryActionClasses}>
                <ArrowRight className="h-4 w-4" />
                Open Studio
              </button>
            </div>
          </div>
        </div>

        <div className="p-5">
          <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
            <div className="rounded-xl border border-borderDefault bg-backgroundInput p-3">
              <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-textSecondary">Balance</p>
              <p className="mt-2 text-base font-semibold tracking-tight text-textPrimary">{balance}</p>
            </div>
            <div className="rounded-xl border border-borderDefault bg-backgroundInput p-3">
              <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-textSecondary">Equity</p>
              <p className="mt-2 text-base font-semibold tracking-tight text-textPrimary">{equity}</p>
            </div>
            <div className="rounded-xl border border-borderDefault bg-backgroundInput p-3">
              <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-textSecondary">Free Margin</p>
              <p className="mt-2 text-base font-semibold tracking-tight text-textPrimary">{freeMargin}</p>
            </div>
            <div className="rounded-xl border border-borderDefault bg-backgroundInput p-3">
              <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-textSecondary">P/L</p>
              <p className={`mt-2 text-base font-semibold tracking-tight ${profitClassName}`}>
                {profit}
              </p>
            </div>
          </div>
        </div>

        <div className="border-t border-borderDefault px-5 py-3">
          <div className="flex flex-wrap items-center gap-2">
            {riskConfigured === false ? (
              <button
                type="button"
                onClick={() => onOpenRisk(account.id)}
                className="rounded-full border border-amber-500/20 bg-amber-500/10 px-2.5 py-1 text-xs font-medium text-amber-300 transition hover:border-amber-500/40 hover:bg-amber-500/15"
              >
                Set risk
              </button>
            ) : null}

            {riskConfigured === true ? (
              <button
                type="button"
                onClick={() => onOpenRisk(account.id)}
                className="rounded-full border border-emerald-500/20 bg-emerald-500/10 px-2.5 py-1 text-xs font-medium text-emerald-300 transition hover:border-emerald-500/40 hover:bg-emerald-500/15"
              >
                Risk configured
              </button>
            ) : null}

            {riskConfigured == null ? (
              <button
                type="button"
                onClick={() => onOpenRisk(account.id)}
                className="rounded-full border border-borderDefault bg-backgroundInput px-2.5 py-1 text-xs font-medium text-textSecondary transition hover:border-primary/30 hover:bg-primary/5 hover:text-textPrimary"
              >
                Risk settings
              </button>
            ) : null}

            {footerAttentionReasons.map((reason) => (
              <span
                key={`${account.id}-${reason}`}
                className="rounded-full border border-borderDefault bg-backgroundInput px-2.5 py-1 text-xs font-medium text-textSecondary"
              >
                {reason}
              </span>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function AccountsHubPreviewPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [accounts, setAccounts] = useState([]);
  const [riskStatus, setRiskStatus] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createPlatform, setCreatePlatform] = useState('MT5');
  const [createModalMode, setCreateModalMode] = useState('standard');
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [selectedAccountTab, setSelectedAccountTab] = useState('edit');
  const [openMenuId, setOpenMenuId] = useState(null);
  const [connectingAccountId, setConnectingAccountId] = useState(null);
  const [vncDetails, setVncDetails] = useState(null);
  const [vncAccountId, setVncAccountId] = useState(null);
  const [vncLoading, setVncLoading] = useState(false);

  const loadRiskStatuses = useCallback(async (accountList) => {
    if (!Array.isArray(accountList) || accountList.length === 0) {
      setRiskStatus({});
      return;
    }

    const results = await Promise.allSettled(accountList.map((account) => fetchRiskSettings(account.id)));
    const nextRiskStatus = {};

    results.forEach((result, index) => {
      const accountId = accountList[index].id;
      if (result.status === 'fulfilled') {
        nextRiskStatus[accountId] = result.value?.max_daily_loss != null;
      } else {
        nextRiskStatus[accountId] = null;
      }
    });

    setRiskStatus(nextRiskStatus);
  }, []);

  const openCreateModal = useCallback((platform = 'MT5', mode = 'standard') => {
    setCreatePlatform(platform);
    setCreateModalMode(mode);
    setShowCreateModal(true);
  }, []);

  const openEditModal = useCallback((accountId, initialTab = 'edit') => {
    const account = accounts.find((item) => String(item.id) === String(accountId));

    if (!account) {
      return;
    }

    setSelectedAccount(account);
    setSelectedAccountTab(initialTab);
  }, [accounts]);

  const closeActionsMenu = useCallback(() => {
    setOpenMenuId(null);
  }, []);

  const loadAccounts = useCallback(async () => {
    setLoading(true);
    setError(null);
    setRiskStatus({});

    try {
      const response = await fetchAccounts();
      const list = normalizeAccounts(response);
      setAccounts(list);
      setLoading(false);
      void loadRiskStatuses(list);
    } catch (loadError) {
      console.error('Failed to load accounts hub preview:', loadError);
      setAccounts([]);
      setRiskStatus({});
      setError('Failed to load accounts.');
      setLoading(false);
    }
  }, [loadRiskStatuses]);

  useEffect(() => {
    void loadAccounts();
  }, [loadAccounts]);

  useEffect(() => {
    const requestedAccountId = location.state?.openAccountId;
    const requestedTab = location.state?.openTab || 'edit';

    if (!requestedAccountId || accounts.length === 0) {
      return;
    }

    openEditModal(requestedAccountId, requestedTab);
    navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
  }, [accounts, location.pathname, location.search, location.state, navigate, openEditModal]);

  useEffect(() => {
    if (searchParams.get('onboarding') !== '1') {
      return;
    }

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('onboarding');
    setSearchParams(nextParams, { replace: true });
  }, [searchParams, setSearchParams]);

  const handleConnect = useCallback(async (account) => {
    closeActionsMenu();

    if (account.platform === 'MT5') {
      let mt5AccountDetails;

      try {
        mt5AccountDetails = await getMT5Account(account.id);
      } catch (error) {
        console.error('Error fetching MT5 account details:', error);
        window.alert('Failed to retrieve MT5 account details.');
        return;
      }

      if (!mt5AccountDetails?.account_number || !mt5AccountDetails?.password || !mt5AccountDetails?.broker_server) {
        window.alert('MT5 account details are incomplete.');
        return;
      }

      setConnectingAccountId(account.id);
      try {
        await connectMT5(account.id, {
          account_number: mt5AccountDetails.account_number,
          password: mt5AccountDetails.password,
          broker_server: mt5AccountDetails.broker_server,
        });
        await loadAccounts();
      } catch (error) {
        console.error('Error connecting to MT5:', error);
        window.alert('Failed to connect to MT5.');
      } finally {
        setConnectingAccountId(null);
      }
      return;
    }

    if (account.platform === 'InteractiveBrokers') {
      setConnectingAccountId(account.id);
      try {
        await connectIB(account.id);
        await loadAccounts();
      } catch (error) {
        console.error('Error connecting to Interactive Brokers:', error);
        window.alert('Failed to connect to Interactive Brokers.');
      } finally {
        setConnectingAccountId(null);
      }
      return;
    }

    if (account.platform === 'cTrader') {
      try {
        const internalAccountId = String(account.id);
        localStorage.setItem('ctrader_pending_internal_account_id', internalAccountId);
        try {
          sessionStorage.setItem('ctrader_pending_internal_account_id', internalAccountId);
        } catch {
          void 0;
        }
        try {
          document.cookie = `ctrader_pending_internal_account_id=${encodeURIComponent(internalAccountId)}; Max-Age=900; Path=/; SameSite=Lax`;
        } catch {
          void 0;
        }

        const popupFeatures = 'width=520,height=720,noopener,noreferrer';
        let popup = null;

        try {
          popup = window.open('about:blank', 'ctraderAuth', popupFeatures);
        } catch {
          popup = null;
        }

        try {
          window.postMessage({ type: 'CTRADER_AUTH_STARTED' }, window.location.origin);
        } catch {
          void 0;
        }

        const data = await onboardStart(account.id);
        const redirectUrl = data?.redirect_url || data?.redirect || data?.url;

        if (redirectUrl) {
          if (popup && !popup.closed) {
            try {
              popup.location.href = redirectUrl;
            } catch {
              window.location.href = redirectUrl;
            }
          } else {
            window.location.href = redirectUrl;
          }
          return;
        }

        if (popup && !popup.closed) {
          try {
            popup.close();
          } catch {
            void 0;
          }
        }
        window.alert('Failed to generate cTrader login URL.');
      } catch (error) {
        console.error('Error generating cTrader login URL:', error);
      }
    }
  }, [closeActionsMenu, loadAccounts]);

  const handleOpenVnc = useCallback(async (account) => {
    if (account.platform !== 'MT5') {
      return;
    }

    closeActionsMenu();
    setVncLoading(true);

    try {
      const response = await startOnboardingAndWait({ internal_account_id: account.id });
      if (response?.vnc_details) {
        setVncDetails(response.vnc_details);
        setVncAccountId(account.id);
      } else {
        window.alert('Failed to start MetaTrader session.');
      }
    } catch (error) {
      console.error('Error starting VNC session:', error);
      window.alert('Failed to start MetaTrader session.');
    } finally {
      setVncLoading(false);
    }
  }, [closeActionsMenu]);

  const handleShutdown = useCallback(async (accountId) => {
    closeActionsMenu();

    try {
      await shutdownInstance(accountId);
      await loadAccounts();
    } catch (error) {
      console.error('Error shutting down account instance:', error);
      window.alert('Failed to shut down the account instance.');
    }
  }, [closeActionsMenu, loadAccounts]);

  const handleRestart = useCallback(async (accountId) => {
    closeActionsMenu();

    try {
      await restartInstance(accountId);
      await loadAccounts();
    } catch (error) {
      console.error('Error restarting account instance:', error);
      window.alert('Failed to restart the account instance.');
    }
  }, [closeActionsMenu, loadAccounts]);

  const closeVncModal = useCallback(() => {
    setVncDetails(null);
    setVncAccountId(null);
  }, []);

  const summary = useMemo(() => {
    const primaryCurrency = accounts.find((account) => account?.currency)?.currency || 'USD';
    const totalBalance = accounts.reduce((sum, account) => sum + (Number.parseFloat(account.balance) || 0), 0);
    const totalEquity = accounts.reduce((sum, account) => sum + (Number.parseFloat(account.equity) || 0), 0);
    const totalFreeMargin = accounts.reduce((sum, account) => sum + (Number.parseFloat(account.free_margin) || 0), 0);
    const totalProfit = accounts.reduce((sum, account) => sum + (Number.parseFloat(account.profit) || 0), 0);
    const offlineCount = accounts.filter((account) => {
      const state = getConnectionState(account);
      return state === 'disconnected' || state === 'inactive';
    }).length;
    const pendingCount = accounts.filter((account) => {
      const state = getConnectionState(account);
      return state === 'connecting' || state === 'pending';
    }).length;
    const riskGapCount = accounts.filter((account) => riskStatus[account.id] === false).length;
    const reviewCount = accounts.filter((account) => buildAttentionReasons(account, riskStatus[account.id]).length > 0).length;
    const reviewHintParts = [];

    if (offlineCount > 0) {
      reviewHintParts.push(`${offlineCount} offline`);
    }

    if (pendingCount > 0) {
      reviewHintParts.push(`${pendingCount} connecting`);
    }

    if (riskGapCount > 0) {
      reviewHintParts.push(`${riskGapCount} risk gaps`);
    }

    return {
      primaryCurrency,
      totalBalance,
      totalEquity,
      totalFreeMargin,
      totalProfit,
      reviewCount,
      reviewHint: reviewHintParts.length > 0 ? reviewHintParts.join(' • ') : 'No flagged accounts',
    };
  }, [accounts, riskStatus]);

  const orderedAccounts = useMemo(() => {
    return [...accounts].sort((left, right) => {
      const leftAttention = buildAttentionReasons(left, riskStatus[left.id]).length;
      const rightAttention = buildAttentionReasons(right, riskStatus[right.id]).length;

      if (leftAttention !== rightAttention) {
        return rightAttention - leftAttention;
      }

      if (Boolean(left.is_default) !== Boolean(right.is_default)) {
        return Number(Boolean(right.is_default)) - Number(Boolean(left.is_default));
      }

      return (Number.parseFloat(right.equity) || 0) - (Number.parseFloat(left.equity) || 0);
    });
  }, [accounts, riskStatus]);

  const hasAccounts = accounts.length > 0;
  const showFirstTimeSetup = !loading && !error && !hasAccounts;

  const handleSkipOnboarding = useCallback(() => {
    try {
      sessionStorage.setItem(FIRST_ACCOUNT_SKIP_KEY, '1');
    } catch {
      void 0;
    }

    navigate('/dashboard');
  }, [navigate]);

  return (
    <div className="mx-auto my-3 max-w-7xl space-y-4 sm:my-4 sm:space-y-5 md:my-6">
      <section className="rounded-2xl border border-borderDefault bg-backgroundSurface px-4 py-4 sm:px-5 sm:py-5">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="max-w-2xl">
            <h1 className="text-2xl font-bold tracking-tight text-textPrimary sm:text-3xl">Operational Accounts Hub</h1>
            <p className="mt-2.5 text-sm text-textSecondary sm:text-base">
              Connection health, balances, and next actions in one place.
            </p>
          </div>

          <div className="flex flex-wrap gap-2.5 lg:justify-end">
            <button type="button" onClick={() => void loadAccounts()} className={secondaryActionClasses}>
              <RefreshCcw className="h-4 w-4 text-primary" />
              Refresh accounts
            </button>
            {hasAccounts ? (
              <button type="button" onClick={() => openCreateModal('MT5', 'standard')} className={primaryActionClasses}>
                <Plus className="h-4 w-4" />
                Add account
              </button>
            ) : null}
          </div>
        </div>

        {!showFirstTimeSetup ? (
          <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <MetricCard
              icon={Wallet}
              label="Total Balance"
              value={formatCurrency(summary.totalBalance, summary.primaryCurrency)}
              hint="Across linked accounts"
            />
            <MetricCard
              icon={BarChart3}
              label="Total Equity"
              value={formatCurrency(summary.totalEquity, summary.primaryCurrency)}
              hint={`${formatSignedCurrency(summary.totalProfit, summary.primaryCurrency)} open P/L`}
            />
            <MetricCard
              icon={ShieldCheck}
              label="Free Margin"
              value={formatCurrency(summary.totalFreeMargin, summary.primaryCurrency)}
              hint="Available margin across accounts"
            />
            <MetricCard
              icon={AlertTriangle}
              label="Needs Review"
              value={String(summary.reviewCount)}
              hint={summary.reviewHint}
            />
          </div>
        ) : null}
      </section>

      <Card className="overflow-hidden rounded-2xl">
        <CardContent className="p-0">
          <div className="flex flex-col gap-3 border-b border-borderDefault px-5 py-4 sm:px-6 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-textPrimary">Accounts</h2>
            </div>
          </div>

          <div className="p-4 sm:p-5">
            {loading ? (
              <div className="rounded-xl border border-borderDefault bg-backgroundInput p-6 text-sm text-textSecondary">
                Loading accounts...
              </div>
            ) : null}

            {!loading && error ? (
              <div className="rounded-xl border border-destructive/30 bg-destructive/10 p-6 text-sm text-destructive">
                {error}
              </div>
            ) : null}

            {showFirstTimeSetup ? (
              <section className="relative overflow-hidden rounded-2xl border border-primary/20 bg-[radial-gradient(circle_at_top_left,_rgba(24,119,242,0.24),_transparent_45%),linear-gradient(135deg,_rgba(15,24,38,0.98),_rgba(17,31,52,0.96))] p-5 shadow-xl sm:p-7">
                <div className="relative z-10 space-y-5">
                  <div className="max-w-2xl space-y-3">
                    <span className="inline-flex items-center rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-xs font-medium uppercase tracking-[0.24em] text-primary">
                      First-time setup
                    </span>
                    <h3 className="text-2xl font-semibold text-white sm:text-3xl">Choose your trading platform</h3>
                    <p className="max-w-xl text-sm text-slate-200/85 sm:text-base">
                      Start with the account you already use. You can add more connections later.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
                    {FIRST_ACCOUNT_PLATFORMS.map((platform) => (
                      <div
                        key={platform.value}
                        role="button"
                        tabIndex={0}
                        aria-label={`Select ${platform.label}`}
                        onClick={() => openCreateModal(platform.value, 'onboarding')}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            openCreateModal(platform.value, 'onboarding');
                          }
                        }}
                        className="group flex min-h-[220px] cursor-pointer flex-col rounded-2xl border border-white/10 bg-white/6 p-4 text-left shadow-lg backdrop-blur transition duration-200 hover:-translate-y-0.5 hover:border-primary/50 hover:bg-white/8 focus:outline-none focus-visible:border-primary/60 focus-visible:ring-2 focus-visible:ring-primary/40"
                      >
                        <div>
                          <div className="text-lg font-semibold text-white">{platform.label}</div>
                          <div className="mt-1 text-xs uppercase tracking-[0.18em] text-primary/90">{platform.badge}</div>
                          <p className="mt-4 text-sm leading-6 text-slate-200/82">{platform.summary}</p>
                          <div className="mt-4">
                            <div className="text-[11px] uppercase tracking-[0.18em] text-slate-400">You'll need</div>
                            <div className="mt-2 flex flex-wrap gap-2">
                              {platform.requirements.map((item) => (
                                <span
                                  key={item}
                                  className="rounded-full border border-white/10 bg-black/10 px-2.5 py-1 text-xs text-slate-200/90"
                                >
                                  {item}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                        <div className="mt-auto flex items-center justify-between pt-6 text-sm font-semibold text-white">
                          <span className="transition group-hover:text-primary group-focus-visible:text-primary">
                            Select {platform.label}
                          </span>
                          <span className="text-primary transition group-hover:translate-x-1 group-focus-visible:translate-x-1">
                            &gt;
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="flex justify-center pt-1">
                    <button
                      type="button"
                      onClick={handleSkipOnboarding}
                      className="text-sm font-medium text-slate-300 transition hover:text-white focus:outline-none focus-visible:text-white"
                    >
                      Skip for now
                    </button>
                  </div>
                </div>
              </section>
            ) : null}

            {!loading && !error && hasAccounts ? (
              <div className="grid grid-cols-1 gap-4">
                {orderedAccounts.map((account) => (
                  <AccountPreviewCard
                    key={account.id}
                    account={account}
                    riskConfigured={riskStatus[account.id]}
                    onOpenEdit={(accountId) => openEditModal(accountId, 'edit')}
                    onOpenRisk={(accountId) => openEditModal(accountId, 'risk')}
                    onOpenHistory={(accountId) => navigate(`/accounts/${accountId}/trade-history`)}
                    onOpenStudio={(accountId) => navigate('/studio', { state: { accountId } })}
                    onToggleActions={(accountId) => setOpenMenuId((currentId) => (currentId === accountId ? null : accountId))}
                    onShutdown={handleShutdown}
                    onRestart={handleRestart}
                    onConnect={handleConnect}
                    onOpenVnc={handleOpenVnc}
                    isActionsOpen={openMenuId === account.id}
                    connectingAccountId={connectingAccountId}
                    vncLoading={vncLoading}
                  />
                ))}
              </div>
            ) : null}
          </div>
        </CardContent>
      </Card>

      {showCreateModal ? (
        <CreateAccountModal
          closeModal={() => setShowCreateModal(false)}
          refreshAccounts={loadAccounts}
          initialPlatform={createPlatform}
          mode={createModalMode}
        />
      ) : null}

      {selectedAccount ? (
        <EditAccountModal
          key={`${selectedAccount.id}:${selectedAccountTab}`}
          account={selectedAccount}
          closeModal={() => setSelectedAccount(null)}
          refreshAccounts={loadAccounts}
          initialTab={selectedAccountTab}
        />
      ) : null}

      {vncLoading ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="flex flex-col items-center gap-3 rounded-lg bg-backgroundSurface p-6 shadow-xl">
            <svg className="h-8 w-8 animate-spin text-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <p className="text-sm font-medium text-textPrimary">Starting MetaTrader session...</p>
          </div>
        </div>
      ) : null}

      {vncDetails && vncAccountId ? (
        <VncModal
          vncDetails={vncDetails}
          internalAccountId={vncAccountId}
          closeModal={closeVncModal}
          refreshAccounts={loadAccounts}
        />
      ) : null}
    </div>
  );
}
